import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { DataUtil } from '../../../assets/ts/_utils';
import { useThemeMode } from '../../../partials/layout/theme-mode/ThemeModeProvider';
import {
  Chart as ChartJS,
  ChartData,
  CategoryScale,
  LogarithmicScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LogarithmicScale, BarElement, BarController, Title, Tooltip, Legend);

type DataPoint = {
  label: string;
  data: number[];
  fill: boolean;
  borderColor: string;
  tension: number;
};

type GameplaySummary = {
  summary: DataPoint[];
  labels?: string[];
  count: number;
  interval?: number;
  extraTooltipLabel?: string;
  extraTooltip?: number[];
};

type Props = {
  gameplayData: GameplaySummary;
  header: string;
};

const LineChart: React.FC<Props> = ({ gameplayData, header }) => {
  const { mode } = useThemeMode();
  const [color, setColor] = useState('#3d3d3d');
  const [gridColor, setGridColor] = useState('#e1e1e1');

  useEffect(() => {
    if (mode === 'dark') {
      setColor('#fff');
      setGridColor('#444444');
    } else {
      setColor('#3d3d3d');
      setGridColor('#e1e1e1');
    }
  }, [mode]);

  const [graphData, setGraphData] = useState<DataPoint[]>([]);
  const [count, setCount] = useState(0);

  function getPreviousNDates(n: number): string[] {
    const result: string[] = [];
    const currentDate = new Date();

    function formatDate(date: Date): string {
      const day = date.getDate() < 10 ? '0' + date.getDate() : date.getDate().toString();
      const month =
        date.getMonth() + 1 < 10
          ? '0' + (date.getMonth() + 1)
          : (date.getMonth() + 1).toString();
      return `${day}/${month}`;
    }

    for (let i = 0; i < n; i++) {
      result.push(formatDate(currentDate));
      currentDate.setDate(currentDate.getDate() - 1);
    }

    return result.reverse();
  }

  useEffect(() => {
    if (gameplayData && gameplayData.summary && gameplayData.summary.length > 0) {
      setGraphData(gameplayData.summary);
      setCount(gameplayData.count);
    }
  }, [gameplayData]);

  const data: ChartData<'bar', number[], string> = {
    labels:
      gameplayData.labels && gameplayData.labels.length > 0
        ? gameplayData.labels
        : getPreviousNDates(count),
    datasets: graphData,
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      point: {
        radius: 4,
        borderWidth: 2,
      },
    },
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (context: any) {
            const value = context.parsed?.y ?? context.raw;
            return `${context.dataset.label || ''}: ${DataUtil.formatNumber(value)}`;
          },
          footer: (tooltipItems: any) => {
            if (!gameplayData.extraTooltip || gameplayData.extraTooltip.length === 0) {
              return '';
            }
            const index = tooltipItems[0]?.dataIndex;
            const extraValue = gameplayData.extraTooltip[index];
            if (extraValue === undefined) {
              return '';
            }
            return `${gameplayData.extraTooltipLabel || 'Extra'}: ${DataUtil.formatNumber(extraValue)}`;
          },
        },
      },
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          color: color,
          boxWidth: 12,
          padding: 10,
        },
      },
      title: {
        display: false,
      },
    },
    color: color,
    scales: {
      y: {
        type: 'logarithmic',
        beginAtZero: true,
        ticks: {
          callback: function (value: number) {
            return DataUtil.formatNumber(value);
          },
          color: color,
          maxTicksLimit: 5,
        },
        grid: {
          color: gridColor,
        },
      },
      x: {
        ticks: {
          color: color,
          maxRotation: 45,
          minRotation: 0,
        },
        grid: {
          color: gridColor,
        },
      },
    },
  };

  return (
    <div style={{ position: 'relative', height: '260px', width: '100%' }}>
      <Bar data={data} options={options} />
    </div>
  );
};

export default LineChart;